import { useState } from "react";
import { KeyRound, Copy, Eye, EyeOff, PlugZap, CheckCircle2, XCircle, RefreshCw, Server, Satellite } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { fmtDateTime } from "../../lib/format";

const GEOTAB_LOGO = "https://customer-assets-jt897jd0.emergentagent.net/job_drive-smart-25/artifacts/ua7ul408_images.png";

const STEPS = [
  { title: "Log masuk ke MyGeotab", desc: "Buka portal MyGeotab di my.geotab.com dan log masuk menggunakan akaun pentadbir fleet agensi anda." },
  { title: "Cipta Akaun Servis", desc: "Pergi ke Administration → Users & Drivers → Users, klik Add. Cipta akaun servis khusus (cth: api-enfleet@agensi.gov.my) dengan clearance \"Data Access — View Only\"." },
  { title: "Dapatkan Nama Database", desc: "Nama database MyGeotab anda dipaparkan pada URL selepas log masuk (cth: my.geotab.com/agensi_anda → database ialah \"agensi_anda\")." },
  { title: "Jana API Key ENFLEET", desc: "Klik butang \"Jana API Key\" di bawah. Key ini digunakan ENFLEET untuk mengesahkan setiap permintaan penyegerakan data. Simpan dengan selamat." },
  { title: "Uji Sambungan", desc: "Isi Pelayan, Database dan Nama Pengguna Akaun Servis, kemudian klik \"Uji Sambungan\" untuk mengesahkan capaian ke pelayan MyGeotab." },
  { title: "Aktifkan Penyegerakan", desc: "Selepas ujian berjaya, hidupkan Penyegerakan Automatik dan pilih data yang ingin disegerak (odometer, lokasi GPS, tingkah laku)." },
];

export default function GeotabSettings({ tenant, reload }) {
  const s = tenant.settings || {};
  const [form, setForm] = useState({
    geotab_server: s.geotab_server || "my.geotab.com",
    geotab_database: s.geotab_database || "",
    geotab_username: s.geotab_username || "",
    geotab_api_key: s.geotab_api_key || "",
    geotab_enabled: !!s.geotab_enabled,
    geotab_sync_interval: s.geotab_sync_interval || 15,
    geotab_sync_odometer: s.geotab_sync_odometer ?? true,
    geotab_sync_location: s.geotab_sync_location ?? true,
    geotab_sync_behaviour: s.geotab_sync_behaviour ?? false,
  });
  const [showKey, setShowKey] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(s.geotab_last_test || null);
  const [saving, setSaving] = useState(false);
  const connected = testResult ? testResult.success : !!s.geotab_connected;

  const persist = async (extra = {}) => {
    const payload = { ...form, ...extra, geotab_sync_interval: Number(form.geotab_sync_interval) };
    await api.put("/settings", payload);
  };

  const generateKey = async () => {
    setGenerating(true);
    try {
      await persist();
      const { data } = await api.post("/settings/geotab/generate-key");
      setForm((f) => ({ ...f, geotab_api_key: data.api_key }));
      setShowKey(true);
      setTestResult(null);
      toast.success("API Key baharu dijana — salin dan simpan dengan selamat");
      reload();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setGenerating(false);
    }
  };

  const copyKey = () => {
    navigator.clipboard.writeText(form.geotab_api_key);
    toast.success("API Key disalin ke papan klip");
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      await persist();
      const { data } = await api.post("/settings/geotab/test-connection");
      setTestResult(data);
      if (data.success) toast.success("Sambungan Geotab berjaya!");
      else toast.error(data.message);
      reload();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setTesting(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await persist();
      toast.success("Tetapan telematik disimpan");
      reload();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <img src={GEOTAB_LOGO} alt="Geotab" className="h-8 w-auto" data-testid="geotab-logo" />
              <div>
                <p className="font-semibold text-sm">Integrasi Telematik Geotab</p>
                <p className="text-xs text-muted-foreground">Segerak odometer, lokasi GPS & tingkah laku pemanduan secara automatik.</p>
              </div>
            </div>
            <Badge data-testid="geotab-status-badge" className={connected ? "bg-[#198754] hover:bg-[#198754]" : "bg-slate-500 hover:bg-slate-500"}>
              {connected ? "Bersambung" : "Belum Disambung"}
            </Badge>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-base">Panduan Integrasi Langkah demi Langkah</CardTitle>
          <CardDescription>Ikuti 6 langkah berikut untuk menyambungkan fleet anda ke MyGeotab.</CardDescription>
        </CardHeader>
        <CardContent>
          <ol className="space-y-3" data-testid="geotab-steps">
            {STEPS.map((step, i) => (
              <li key={i} className="flex gap-3">
                <span className="h-6 w-6 rounded-full bg-[#10056E] text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                <div>
                  <p className="text-sm font-semibold">{step.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{step.desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-base flex items-center gap-2"><Server className="h-4 w-4" /> Maklumat Sambungan</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Pelayan (Server)</Label>
              <Input data-testid="geotab-server-input" value={form.geotab_server}
                onChange={(e) => setForm({ ...form, geotab_server: e.target.value })} placeholder="my.geotab.com" />
            </div>
            <div className="space-y-1.5">
              <Label>Pangkalan Data (Database)</Label>
              <Input data-testid="geotab-database-input" value={form.geotab_database}
                onChange={(e) => setForm({ ...form, geotab_database: e.target.value })} placeholder="agensi_anda" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Nama Pengguna Akaun Servis</Label>
            <Input data-testid="geotab-username-input" value={form.geotab_username}
              onChange={(e) => setForm({ ...form, geotab_username: e.target.value })} placeholder="api-enfleet@agensi.gov.my" />
          </div>
          <div className="space-y-1.5">
            <Label>API Key ENFLEET</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <KeyRound className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input data-testid="geotab-apikey-input" readOnly type={showKey ? "text" : "password"}
                  className="pl-9 pr-9 font-mono text-xs" value={form.geotab_api_key} placeholder="Belum dijana — klik Jana API Key" />
                <button type="button" onClick={() => setShowKey(!showKey)} data-testid="geotab-apikey-toggle-visibility"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <Button type="button" variant="outline" size="icon" onClick={copyKey} disabled={!form.geotab_api_key} data-testid="geotab-apikey-copy-btn">
                <Copy className="h-4 w-4" />
              </Button>
              <Button type="button" variant="outline" onClick={generateKey} disabled={generating} data-testid="geotab-generate-key-btn">
                <RefreshCw className={`h-4 w-4 mr-2 ${generating ? "animate-spin" : ""}`} />
                {form.geotab_api_key ? "Jana Semula" : "Jana API Key"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">Menjana key baharu akan membatalkan key lama serta-merta.</p>
          </div>

          <div className="pt-1">
            <Button onClick={testConnection} disabled={testing} className="bg-[#10056E] hover:bg-[#2a17a8]" data-testid="geotab-test-connection-btn">
              <PlugZap className="h-4 w-4 mr-2" /> {testing ? "Menguji sambungan..." : "Uji Sambungan"}
            </Button>
          </div>

          {testResult && (
            <div data-testid="geotab-test-result"
              className={`border rounded-lg p-4 ${testResult.success ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}`}>
              <div className="flex items-center gap-2">
                {testResult.success
                  ? <CheckCircle2 className="h-5 w-5 text-[#198754]" />
                  : <XCircle className="h-5 w-5 text-[#DC3545]" />}
                <p className={`text-sm font-semibold ${testResult.success ? "text-[#146c43]" : "text-[#b02a37]"}`}>{testResult.message}</p>
              </div>
              {testResult.success && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 text-xs">
                  <div><p className="text-muted-foreground">Versi Pelayan</p><p className="font-semibold mt-0.5">{testResult.server_version}</p></div>
                  <div><p className="text-muted-foreground">Database</p><p className="font-semibold mt-0.5">{testResult.database}</p></div>
                  <div><p className="text-muted-foreground">Peranti Dikesan</p><p className="font-semibold mt-0.5">{testResult.devices_detected} unit</p></div>
                  <div><p className="text-muted-foreground">Latensi</p><p className="font-semibold mt-0.5">{testResult.latency_ms} ms</p></div>
                </div>
              )}
              <p className="text-[11px] text-muted-foreground mt-2">Diuji pada {fmtDateTime(testResult.tested_at)}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="font-heading text-base flex items-center gap-2"><Satellite className="h-4 w-4" /> Penyegerakan Data</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between border rounded-lg p-3">
            <div>
              <p className="text-sm font-semibold">Penyegerakan Automatik</p>
              <p className="text-xs text-muted-foreground">Tarik data terkini dari MyGeotab mengikut selang masa di bawah.</p>
            </div>
            <Switch checked={form.geotab_enabled} onCheckedChange={(v) => setForm({ ...form, geotab_enabled: v })} data-testid="settings-geotab-toggle" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Selang Penyegerakan</Label>
              <Select value={String(form.geotab_sync_interval)} onValueChange={(v) => setForm({ ...form, geotab_sync_interval: Number(v) })}>
                <SelectTrigger data-testid="geotab-interval-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="5">Setiap 5 minit</SelectItem>
                  <SelectItem value="15">Setiap 15 minit</SelectItem>
                  <SelectItem value="30">Setiap 30 minit</SelectItem>
                  <SelectItem value="60">Setiap 1 jam</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {[
            { key: "geotab_sync_odometer", title: "Odometer", desc: "Kemas kini bacaan odometer kenderaan secara automatik dalam Buku Log." },
            { key: "geotab_sync_location", title: "Lokasi GPS", desc: "Paparkan kedudukan langsung kenderaan pada peta telematik." },
            { key: "geotab_sync_behaviour", title: "Tingkah Laku Pemanduan", desc: "Brek mengejut, pecutan agresif dan had laju (memerlukan pelan Geotab Pro)." },
          ].map((r) => (
            <div key={r.key} className="flex items-center justify-between border rounded-lg p-3">
              <div>
                <p className="text-sm font-semibold">{r.title}</p>
                <p className="text-xs text-muted-foreground">{r.desc}</p>
              </div>
              <Switch checked={form[r.key]} onCheckedChange={(v) => setForm({ ...form, [r.key]: v })} data-testid={`geotab-sync-${r.key.split("_").pop()}`} />
            </div>
          ))}
        </CardContent>
      </Card>

      <Button onClick={save} disabled={saving} className="bg-[#10056E] hover:bg-[#2a17a8]" data-testid="settings-save-btn">
        {saving ? "Menyimpan..." : "Simpan Tetapan"}
      </Button>
    </div>
  );
}
